import {
    createOptimizerServiceManager,
    OptimizerServiceManagerReturn,
} from '@/optimizer/createOptimizerServiceManager';
import { createLogger } from '@/optimizer/helpers/createLogger';
import { toInitError } from '@/optimizer/helpers/toInitError';
import { withTimeout } from '@/optimizer/helpers/withTimeout';
import { env } from '@/utils';
import {
    createOptimizerWorkerClient,
    createWorkerOptimizerPort,
    type OptimizerPort,
    type PyodideConstructableOptions,
} from '@analytical-alley/opt-wasm';

export let optimizerService: OptimizerPort;
export let optimizerServiceManager: {
    service: OptimizerPort;
    serviceManager: OptimizerServiceManagerReturn;
} | null = null;

let optimizerWorkerClient: ReturnType<
    typeof createOptimizerWorkerClient
> | null = null;
let optimizerWarmupPromise: Promise<void> | null = null;
let optimizerInitPromise: Promise<{
    service: OptimizerPort;
    serviceManager: OptimizerServiceManagerReturn;
}> | null = null;

const DEFAULT_OPTIONS: PyodideConstructableOptions = {
    optimiserWheelPath: env.VITE_OPT_CORE_WHEEL_URL,
    duckDbWheelUrl: env.VITE_DUCKDB_WHEEL_URL,
};

interface InitOptimizerOptions {
    consoleEnabled: boolean;
    duckDbPath?: string;
    excelPath?: string;
    jsonConfig?: object;
}

const OPTIMIZER_INIT_TIMEOUT_MS = 90000;
const OPTIMIZER_WARMUP_TIMEOUT_MS = 20000;

const getOptimizerWorkerClient = () => {
    if (!optimizerWorkerClient) {
        optimizerWorkerClient = createOptimizerWorkerClient();
    }

    return optimizerWorkerClient;
};

export const prewarmOptimizerRuntime = async () => {
    if (optimizerWarmupPromise) {
        return optimizerWarmupPromise;
    }

    optimizerWarmupPromise = (async () => {
        try {
            await withTimeout(
                getOptimizerWorkerClient().api.warmup(),
                OPTIMIZER_WARMUP_TIMEOUT_MS,
            );
        } catch (error) {
            optimizerWarmupPromise = null;
            throw toInitError(error);
        }
    })();

    return optimizerWarmupPromise;
};

async function initWorkerOptimizer(
    duckDbPath: string | undefined,
    excelPath: string | undefined,
    pyodideOptions: PyodideConstructableOptions,
) {
    const initStartedAt = performance.now();
    const { api } = getOptimizerWorkerClient();
    await prewarmOptimizerRuntime();

    const { service } = await withTimeout(
        createWorkerOptimizerPort(
            {
                options: pyodideOptions,
                duckDbPath,
                excelPath,
            },
            () => api.warmup(),
            getOptimizerWorkerClient(),
        ),
        OPTIMIZER_INIT_TIMEOUT_MS,
    ).catch((error) => {
        throw toInitError(error);
    });

    // eslint-disable-next-line no-console
    console.info('[optimizer] init timing', {
        durationMs: Math.round(performance.now() - initStartedAt),
    });

    return {
        service,
        serviceManager: await createOptimizerServiceManager(service),
    };
}

export async function initOptimizer(
    {
        consoleEnabled,
        duckDbPath,
        excelPath,
        jsonConfig,
    }: InitOptimizerOptions = {
        consoleEnabled: env.DEV,
    },
) {
    const logger = createLogger(consoleEnabled);

    if (optimizerInitPromise) {
        return optimizerInitPromise;
    }

    if (!!optimizerService || !!optimizerServiceManager) {
        logger.warn('Optimizer service is already initialized!');
        return;
    }

    const pyodideOptions: PyodideConstructableOptions = {
        ...DEFAULT_OPTIONS,
        jsonConfig,
    };

    optimizerInitPromise = (async () => {
        try {
            const { service, serviceManager } = await initWorkerOptimizer(
                duckDbPath,
                excelPath,
                pyodideOptions,
            );
            optimizerService = service;
            optimizerServiceManager = { service, serviceManager };
            return { service, serviceManager };
        } catch (error) {
            optimizerInitPromise = null;
            throw error;
        }
    })();

    return optimizerInitPromise;
}
